import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Pagination
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import useMonthlyUnpaid from './useMonthlyUnpaid';
import useBankTransferOnly from '../../../hooks/useBankTransferOnly';
import moment from 'moment';

const MonthlyUnpaid = () => {
  // Payment links are off while BANK_TRANSFER_ONLY is set — hide the generate button
  const bankTransferOnly = useBankTransferOnly();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get month from URL or default to current month
  const urlMonth = searchParams.get('month');
  const urlYear = searchParams.get('year');
  const defaultMonth = moment().format('YYYY-MM');
  
  const [selectedMonth, setSelectedMonth] = useState(
    urlMonth && urlYear ? `${urlYear}-${urlMonth.padStart(2, '0')}` : defaultMonth
  );
  
  // Calculate asOfDate (end of selected month)
  const asOfDate = moment(selectedMonth).endOf('month').format('YYYY-MM-DD');
  
  const {
    data, loading, generatingLink, paidCustomerIds, refetch, handleGeneratePaymentLink,
    page, setPage, status, changeStatus, pagination
  } = useMonthlyUnpaid(asOfDate);
  const customers = data?.customers || [];

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    setPage(1);

    // Update URL with new month/year
    const [newYear, newMonthNum] = newMonth.split('-');
    setSearchParams({
      month: newMonthNum,
      year: newYear
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY');
  };

  const getPaymentStatusChip = (paymentStatus) => {
    const getChipProps = (status) => {
      switch (status) {
        case 'Payment Needed':
          return { label: 'จ่ายเพิ่ม', color: 'success' };
        case 'Fully Paid':
          return { label: 'ไม่ต้องจ่าย', color: 'warning' };
        default:
          return { label: status, color: 'default' };
      }
    };
    
    const chipProps = getChipProps(paymentStatus);
    return (
      <Chip 
        label={chipProps.label}
        color={chipProps.color}
        size="small"
      />
    );
  };

  const getOrderPaymentChip = (paymentNeeded) => {
    return (
      <Chip 
        label={paymentNeeded ? 'จ่ายเพิ่ม' : 'ไม่ต้องจ่าย'} 
        color={paymentNeeded ? 'success' : 'warning'} 
        size="small"
      />
    );
  };

  const openCustomerProfileInNewTab = (smileName, shop) => {
    if (!smileName) return;
    let url = `/customer-profile?smileName=${encodeURIComponent(smileName)}`;
    if (shop) url += `&shop=${encodeURIComponent(shop)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };


  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
          รายการค้างจ่ายบริษัท
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Month Selector */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="month-select-label">เดือน</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              onChange={handleMonthChange}
              label="เดือน"
            >
              {Array.from({ length: 12 }, (_, i) => moment().subtract(i, 'months')).map((m) => (
                <MenuItem key={m.format('YYYY-MM')} value={m.format('YYYY-MM')}>
                  {m.format('MMMM YYYY')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel id="status-select-label">สถานะ</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={status}
              onChange={(e) => changeStatus(e.target.value)}
              label="สถานะ"
            >
              <MenuItem value="paymentNeeded">จ่ายเพิ่ม</MenuItem>
              <MenuItem value="all">ทั้งหมด</MenuItem>
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
          >
            รีเฟรช
          </Button>
        </Box>
      </Box>


      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* Table */}
          <TableContainer component={Paper} elevation={3}>
            {customers.length === 0 ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  ไม่พบรายการค้างจ่ายในเดือนนี้
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>วันที่</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Invoice No</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ลูกค้า</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Smile Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ยอดรวม</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>จ่ายแล้ว</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>ค้างจ่าย</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>สถานะ</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>การดำเนินการ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => {
                    // Paid AFTER this list was fetched (webhook/refused generate): green
                    // rows + locked generate button until the admin refreshes.
                    const paidLive = paidCustomerIds.has(customer.customerId);
                    return (
                    <React.Fragment key={`${customer.customerId}-${customer.paymentStatus}`}>
                      {/* Customer Header Row */}
                      <TableRow sx={{ backgroundColor: paidLive ? '#e8f5e9' : '#f9f9f9', borderBottom: '2px solid #e0e0e0' }}>
                        <TableCell colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: paidLive ? '#c8e6c9' : '#e3f2fd' }}>
                          <Box
                            component="span"
                            role="link"
                            tabIndex={0}
                            onClick={() => openCustomerProfileInNewTab(customer.smileName, customer.shop)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openCustomerProfileInNewTab(customer.smileName, customer.shop);
                              }
                            }}
                            sx={{
                              cursor: customer.smileName ? 'pointer' : 'default',
                              textDecoration: customer.smileName ? 'underline' : 'none',
                              textUnderlineOffset: '3px',
                              '&:hover': { opacity: customer.smileName ? 0.8 : 1 }
                            }}
                          >
                            {customer.customerName} (ID: {customer.customerId}) - {customer.smileName}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                          {formatAmount(customer.summary.totalCompanyAmount)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: 'success.main' }}>
                          {formatAmount(customer.summary.paidToCompany)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: 'error.main' }}>
                          {formatAmount(customer.summary.unpaidToCompany)}
                        </TableCell>
                        <TableCell>
                          {paidLive
                            ? <Chip label="ชำระแล้ว — รีเฟรชเพื่ออัปเดต" color="success" size="small" />
                            : getPaymentStatusChip(customer.paymentStatus)}
                        </TableCell>
                        <TableCell>
                          {customer.paymentStatus === 'Payment Needed' && !bankTransferOnly && (
                            <Tooltip title={paidLive ? 'ชำระแล้ว — สร้างลิงก์ไม่ได้' : 'สร้าง Payment Link'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleGeneratePaymentLink(customer.customerId)}
                                  disabled={generatingLink || paidLive}
                                >
                                  <PaymentIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      
                      {/* Customer Orders */}
                      {customer.orders.map((order, index) => (
                        <TableRow key={`${customer.customerId}-${index}`} sx={{ pl: 2, backgroundColor: paidLive ? '#e8f5e9' : undefined }}>
                          <TableCell sx={{ pl: 4, textAlign: 'center' }}>
                            {formatDate(order.orderDate)}
                          </TableCell>
                          <TableCell sx={{ pl: 4, textAlign: 'center' }}>
                            {/* Click the invoice/order code to open the product URL */}
                            {order.url && order.url !== '-' ? (
                              <Box
                                component="a"
                                href={order.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  color: 'inherit',
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '3px',
                                  '&:hover': { opacity: 0.8 }
                                }}
                              >
                                {order.invoiceNo}
                              </Box>
                            ) : (
                              order.invoiceNo
                            )}
                          </TableCell>
                          <TableCell sx={{ pl: 4, textAlign: 'center' }}>
                            <Box
                              component="span"
                              role="link"
                              tabIndex={0}
                              onClick={() => openCustomerProfileInNewTab(customer.smileName, customer.shop)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openCustomerProfileInNewTab(customer.smileName, customer.shop);
                                }
                              }}
                              sx={{
                                cursor: customer.smileName ? 'pointer' : 'default',
                                textDecoration: customer.smileName ? 'underline' : 'none',
                                textUnderlineOffset: '3px',
                                '&:hover': { opacity: customer.smileName ? 0.8 : 1 }
                              }}
                            >
                              {customer.customerName}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ pl: 4, textAlign: 'center' }}>
                            {order.smileName}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            {formatAmount(order.totalCompanyAmount)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', color: 'success.main' }}>
                            {formatAmount(order.paidToCompany)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', color: 'error.main' }}>
                            {formatAmount(order.unpaidToCompany)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}></TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                disabled={loading}
              />
              <Typography variant="body2" color="text.secondary">
                {pagination.totalCustomers} ลูกค้า
              </Typography>
            </Box>
          )}

        </>
      )}
    </Box>
  );
};

export default MonthlyUnpaid;
